/**
 * Production-ready performance monitoring service for comprehensive metrics collection.
 * 
 * This service provides:
 * - Page load time monitoring
 * - API call performance tracking
 * - User interaction metrics
 * - Cache hit/miss rate monitoring
 * - Error rate tracking
 * - Memory usage monitoring
 * 
 * @example
 * ```typescript
 * import { PerformanceMonitor } from './performance';
 * 
 * const monitor = PerformanceMonitor.getInstance();
 * const timer = monitor.startTimer('loadPage');
 * // ... perform operation
 * monitor.endTimer(timer, { success: true, metadata: { pageNum: 1 } });
 * ```
 */

import { logger, LogContext } from './logger';

/**
 * Performance timer interface
 */
export interface PerformanceTimer {
  id: string;
  operation: string;
  startTime: number;
  startMemory?: number;
  correlationId: string;
}

/**
 * Performance metrics interface
 */
export interface PerformanceMetrics {
  operation: string;
  duration: number;
  success: boolean;
  error?: string;
  memoryUsed?: number;
  metadata?: Record<string, any>;
}

/**
 * Cache metrics interface
 */
export interface CacheMetrics {
  hits: number;
  misses: number;
  hitRate: number;
  totalRequests: number;
}

/**
 * User interaction metrics interface
 */
export interface UserInteractionMetrics {
  action: string;
  element: string;
  timestamp: number;
  duration?: number;
  metadata?: Record<string, any>;
}

/**
 * API call metrics interface
 */
export interface ApiCallMetrics {
  method: string;
  url: string;
  status: number;
  duration: number;
  size?: number;
  cached?: boolean;
}

/**
 * Production-ready performance monitoring service
 */
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private timers: Map<string, PerformanceTimer> = new Map();
  private cacheStats: Map<string, CacheMetrics> = new Map();
  private userInteractions: UserInteractionMetrics[] = [];
  private apiCalls: ApiCallMetrics[] = [];
  private errorCount = 0;
  private totalOperations = 0;

  private constructor() {
    this.initializeWebVitals();
    this.setupPerformanceObserver();
  }

  /**
   * Get singleton performance monitor instance
   */
  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Start a performance timer for an operation
   * 
   * @param operation - Operation name for categorization
   * @param correlationId - Optional correlation ID for tracing
   * @returns Performance timer object
   */
  public startTimer(operation: string, correlationId?: string): PerformanceTimer {
    const id = `${operation}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timer: PerformanceTimer = {
      id,
      operation,
      startTime: performance.now(),
      startMemory: this.getMemoryUsage(),
      correlationId: correlationId || logger.getCorrelationId()
    };

    this.timers.set(id, timer);

    logger.debug('Performance timer started', {
      correlationId: timer.correlationId,
      operation: 'startTimer',
      metadata: {
        timerId: id,
        operation,
        startTime: timer.startTime,
        startMemory: timer.startMemory
      }
    });

    return timer;
  }

  /**
   * End a performance timer and log metrics
   * 
   * @param timer - Timer object from startTimer
   * @param result - Operation result details
   */
  public endTimer(
    timer: PerformanceTimer,
    result: { success: boolean; error?: string; metadata?: Record<string, any> }
  ): PerformanceMetrics {
    const endTime = performance.now();
    const duration = endTime - timer.startTime;
    const endMemory = this.getMemoryUsage();
    const memoryUsed = endMemory && timer.startMemory ? endMemory - timer.startMemory : undefined;

    const metrics: PerformanceMetrics = {
      operation: timer.operation,
      duration,
      success: result.success,
      error: result.error,
      memoryUsed,
      metadata: result.metadata
    };

    // Update counters
    this.totalOperations++;
    if (!result.success) {
      this.errorCount++;
    }

    // Log performance metrics
    const logLevel = result.success ? 'info' : 'warn';
    logger[logLevel]('Performance timer completed', {
      correlationId: timer.correlationId,
      operation: 'endTimer',
      duration,
      metadata: {
        timerId: timer.id,
        operation: timer.operation,
        success: result.success,
        error: result.error,
        memoryUsed,
        errorRate: this.getErrorRate(),
        ...result.metadata
      }
    });

    // Remove timer from active timers
    this.timers.delete(timer.id);

    // Send metrics to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      this.sendMetricsToService(metrics, timer.correlationId);
    }

    return metrics;
  }

  /**
   * Track API call performance
   * 
   * @param method - HTTP method
   * @param url - Request URL
   * @param status - Response status code
   * @param duration - Request duration in milliseconds
   * @param size - Response size in bytes
   * @param cached - Whether response was cached
   * @param correlationId - Correlation ID for tracing
   */
  public trackApiCall(
    method: string,
    url: string,
    status: number,
    duration: number,
    size?: number,
    cached?: boolean,
    correlationId?: string
  ): void {
    const metrics: ApiCallMetrics = {
      method,
      url,
      status,
      duration,
      size,
      cached
    };

    this.apiCalls.push(metrics);

    // Keep only last 100 API calls to prevent memory leaks
    if (this.apiCalls.length > 100) {
      this.apiCalls.shift();
    }

    logger.logApiCall(method, url, status, duration, {
      correlationId: correlationId || logger.getCorrelationId(),
      metadata: {
        size,
        cached,
        apiCallCount: this.apiCalls.length
      }
    });
  }

  /**
   * Track user interaction for analytics
   * 
   * @param action - Action performed (click, scroll, etc.)
   * @param element - Element interacted with
   * @param duration - Optional interaction duration
   * @param metadata - Additional metadata
   * @param correlationId - Correlation ID for tracing
   */
  public trackUserInteraction(
    action: string,
    element: string,
    duration?: number,
    metadata?: Record<string, any>,
    correlationId?: string
  ): void {
    const interaction: UserInteractionMetrics = {
      action,
      element,
      timestamp: Date.now(),
      duration,
      metadata
    };

    this.userInteractions.push(interaction);

    // Keep only last 50 interactions to prevent memory leaks
    if (this.userInteractions.length > 50) {
      this.userInteractions.shift();
    }

    logger.logUserInteraction(action, element, {
      correlationId: correlationId || logger.getCorrelationId(),
      metadata: {
        duration,
        interactionCount: this.userInteractions.length,
        ...metadata
      }
    });
  }

  /**
   * Track cache hit/miss for performance monitoring
   * 
   * @param cacheKey - Cache key identifier
   * @param hit - Whether it was a cache hit or miss
   * @param correlationId - Correlation ID for tracing
   */
  public trackCacheAccess(cacheKey: string, hit: boolean, correlationId?: string): void {
    let stats = this.cacheStats.get(cacheKey);
    if (!stats) {
      stats = { hits: 0, misses: 0, hitRate: 0, totalRequests: 0 };
      this.cacheStats.set(cacheKey, stats);
    }

    if (hit) {
      stats.hits++;
    } else {
      stats.misses++;
    }

    stats.totalRequests = stats.hits + stats.misses;
    stats.hitRate = stats.totalRequests > 0 ? stats.hits / stats.totalRequests : 0;

    logger.debug('Cache access tracked', {
      correlationId: correlationId || logger.getCorrelationId(),
      operation: 'trackCacheAccess',
      metadata: {
        cacheKey,
        hit,
        hitRate: stats.hitRate,
        totalRequests: stats.totalRequests
      }
    });
  }

  /**
   * Get cache statistics for a specific key
   * 
   * @param cacheKey - Cache key to get stats for
   * @returns Cache metrics or undefined if not found
   */
  public getCacheStats(cacheKey: string): CacheMetrics | undefined {
    return this.cacheStats.get(cacheKey);
  }

  /**
   * Get overall cache statistics
   * 
   * @returns Aggregated cache metrics
   */
  public getOverallCacheStats(): CacheMetrics {
    let totalHits = 0;
    let totalMisses = 0;

    for (const stats of Array.from(this.cacheStats.values())) {
      totalHits += stats.hits;
      totalMisses += stats.misses;
    }

    const totalRequests = totalHits + totalMisses;
    const hitRate = totalRequests > 0 ? totalHits / totalRequests : 0;

    return {
      hits: totalHits,
      misses: totalMisses,
      hitRate,
      totalRequests
    };
  }

  /**
   * Get current error rate
   * 
   * @returns Error rate as percentage (0-1)
   */
  public getErrorRate(): number {
    return this.totalOperations > 0 ? this.errorCount / this.totalOperations : 0;
  }

  /**
   * Get performance summary for monitoring dashboard
   * 
   * @returns Performance summary object
   */
  public getPerformanceSummary(): {
    errorRate: number;
    totalOperations: number;
    cacheStats: CacheMetrics;
    recentApiCalls: ApiCallMetrics[];
    recentInteractions: UserInteractionMetrics[];
    memoryUsage?: number;
  } {
    return {
      errorRate: this.getErrorRate(),
      totalOperations: this.totalOperations,
      cacheStats: this.getOverallCacheStats(),
      recentApiCalls: this.apiCalls.slice(-10), // Last 10 API calls
      recentInteractions: this.userInteractions.slice(-10), // Last 10 interactions
      memoryUsage: this.getMemoryUsage()
    };
  }

  /**
   * Log performance summary for monitoring
   * 
   * @param correlationId - Correlation ID for tracing
   */
  public logPerformanceSummary(correlationId?: string): void {
    const summary = this.getPerformanceSummary();

    logger.info('Performance summary', {
      correlationId: correlationId || logger.getCorrelationId(),
      operation: 'performanceSummary',
      metadata: summary
    });
  }

  /**
   * Get current memory usage if available
   * 
   * @returns Memory usage in MB or undefined if not available
   */
  private getMemoryUsage(): number | undefined {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return Math.round(memory.usedJSHeapSize / 1024 / 1024 * 100) / 100; // MB with 2 decimal places
    }
    return undefined;
  }

  /**
   * Initialize Web Vitals monitoring
   */
  private initializeWebVitals(): void {
    // Monitor Core Web Vitals if available
    if ('PerformanceObserver' in window) {
      try {
        // Largest Contentful Paint (LCP)
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            logger.logPerformance('LCP', entry.startTime, {
              correlationId: logger.getCorrelationId(),
              metadata: {
                element: (entry as any).element?.tagName,
                url: entry.name
              }
            });
          }
        }).observe({ entryTypes: ['largest-contentful-paint'] });

        // First Input Delay (FID)
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            logger.logPerformance('FID', (entry as any).processingStart - entry.startTime, {
              correlationId: logger.getCorrelationId(),
              metadata: {
                eventType: (entry as any).name
              }
            });
          }
        }).observe({ entryTypes: ['first-input'] });

        // Cumulative Layout Shift (CLS)
        new PerformanceObserver((list) => {
          let clsValue = 0;
          for (const entry of list.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              clsValue += (entry as any).value;
            }
          }
          if (clsValue > 0) {
            logger.logPerformance('CLS', clsValue, {
              correlationId: logger.getCorrelationId()
            });
          }
        }).observe({ entryTypes: ['layout-shift'] });
      } catch (error) {
        logger.warn('Failed to initialize Web Vitals monitoring', {
          correlationId: logger.getCorrelationId(),
          operation: 'initializeWebVitals',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
  }

  /**
   * Setup performance observer for navigation and resource timing
   */
  private setupPerformanceObserver(): void {
    if ('PerformanceObserver' in window) {
      try {
        // Monitor navigation timing
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const navEntry = entry as PerformanceNavigationTiming;
            logger.logPerformance('pageLoad', navEntry.loadEventEnd - navEntry.startTime, {
              correlationId: logger.getCorrelationId(),
              metadata: {
                domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.startTime,
                firstPaint: navEntry.responseEnd - navEntry.startTime,
                type: navEntry.type
              }
            });
          }
        }).observe({ entryTypes: ['navigation'] });

        // Monitor resource timing for large resources
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const resourceEntry = entry as PerformanceResourceTiming;
            // Only log resources that took more than 100ms
            if (resourceEntry.duration > 100) {
              logger.logPerformance('resourceLoad', resourceEntry.duration, {
                correlationId: logger.getCorrelationId(),
                metadata: {
                  name: resourceEntry.name,
                  type: resourceEntry.initiatorType,
                  size: resourceEntry.transferSize
                }
              });
            }
          }
        }).observe({ entryTypes: ['resource'] });
      } catch (error) {
        logger.warn('Failed to setup performance observer', {
          correlationId: logger.getCorrelationId(),
          operation: 'setupPerformanceObserver',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
  }

  /**
   * Send metrics to external monitoring service
   * 
   * @param metrics - Performance metrics to send
   * @param correlationId - Correlation ID for tracing
   */
  private async sendMetricsToService(metrics: PerformanceMetrics, correlationId: string): Promise<void> {
    try {
      const metricsEndpoint = process.env.REACT_APP_METRICS_ENDPOINT;
      if (metricsEndpoint) {
        await fetch(metricsEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...metrics,
            correlationId,
            timestamp: Date.now(),
            userAgent: navigator.userAgent,
            url: window.location.href
          })
        });
      }
    } catch (error) {
      // Fail silently to avoid affecting user experience
      logger.debug('Failed to send metrics to external service', {
        correlationId,
        operation: 'sendMetricsToService',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }
}

// Export singleton instance for convenience
export const performanceMonitor = PerformanceMonitor.getInstance();
export default performanceMonitor;